import { appEvents, appEventTitle, resolveInteractionEventName } from "@slon/bdui-document";

import { findWidgetVisualKey } from "../../selection/phase-node.js";

export type WidgetContractRowKind = "field" | "widget_event" | "global_event" | "id";

export interface WidgetContractFieldRow {
  kind: "field";
  name: string;
  title: string;
}

export interface WidgetContractEventRow {
  kind: "widget_event" | "global_event";
  name: string;
  title: string;
}

export interface WidgetContractIdRow {
  kind: "id";
  id: string;
  title: string;
  /** Container id is present on view or phase trees. */
  used: boolean;
}

export type WidgetContractRow = WidgetContractFieldRow | WidgetContractEventRow | WidgetContractIdRow;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export { resolveInteractionEventName };

function parseInterface(spec: Record<string, unknown> | null | undefined): {
  fields: WidgetContractFieldRow[];
  events: WidgetContractEventRow[];
  ids: Omit<WidgetContractIdRow, "used">[];
} {
  const raw = spec?.interface;
  if (!isRecord(raw)) return { fields: [], events: [], ids: [] };
  const fields: WidgetContractFieldRow[] = [];
  if (Array.isArray(raw.fields)) {
    for (const item of raw.fields) {
      if (isRecord(item) && typeof item.name === "string" && typeof item.title === "string") {
        fields.push({ kind: "field", name: item.name, title: item.title });
      }
    }
  }
  const events: WidgetContractEventRow[] = [];
  if (Array.isArray(raw.events)) {
    for (const item of raw.events) {
      if (isRecord(item) && typeof item.name === "string" && typeof item.title === "string") {
        events.push({ kind: "widget_event", name: item.name, title: item.title });
      }
    }
  }
  const ids: Omit<WidgetContractIdRow, "used">[] = [];
  if (Array.isArray(raw.ids)) {
    for (const item of raw.ids) {
      if (isRecord(item) && typeof item.id === "string" && typeof item.title === "string") {
        ids.push({ kind: "id", id: item.id, title: item.title });
      }
    }
  }
  return { fields, events, ids };
}

function collectBlockIds(value: unknown, ids: Set<string>): void {
  if (Array.isArray(value)) {
    for (const item of value) collectBlockIds(item, ids);
    return;
  }
  if (!isRecord(value)) return;
  if (value.type === "container" && typeof value.id === "string" && value.id.length > 0) {
    ids.add(value.id);
  }
  for (const child of Object.values(value)) {
    if (child && typeof child === "object") collectBlockIds(child, ids);
  }
}

function collectUsedBlockIds(spec: Record<string, unknown> | null | undefined): Set<string> {
  const ids = new Set<string>();
  if (!spec) return ids;
  collectBlockIds(spec.view, ids);
  collectBlockIds(spec.elements, ids);
  if (Array.isArray(spec.phases)) {
    for (const phase of spec.phases) collectBlockIds(phase, ids);
  }
  return ids;
}

function walkTree(value: unknown, visit: (node: Record<string, unknown>) => void): void {
  if (Array.isArray(value)) {
    for (const item of value) walkTree(item, visit);
    return;
  }
  if (!isRecord(value)) return;
  if (value.type || value.items || value.interaction || value.appearance) visit(value);
  for (const child of Object.values(value)) walkTree(child, visit);
}

function collectGlobalEventNames(spec: Record<string, unknown> | null | undefined): Set<string> {
  const names = new Set<string>();
  walkTree(spec?.view, (node) => {
    const eventName = resolveInteractionEventName(node.interaction);
    if (eventName) names.add(eventName);
  });
  if (Array.isArray(spec?.phases)) {
    for (const phase of spec.phases) walkTree(phase, (node) => {
      const eventName = resolveInteractionEventName(node.interaction);
      if (eventName) names.add(eventName);
    });
  }
  return names;
}

function toGlobalRow(event: { name: string; title: string }): WidgetContractEventRow {
  return {
    kind: "global_event",
    name: event.name,
    title: appEventTitle(event.name) ?? event.title,
  };
}

/** Fields, internal widget events, and the full app event catalog split by node wiring. */
export function collectWidgetContract(spec: Record<string, unknown> | null | undefined): {
  fields: WidgetContractFieldRow[];
  ids: WidgetContractIdRow[];
  widgetEvents: WidgetContractEventRow[];
  globalEvents: WidgetContractEventRow[];
  otherGlobalEvents: WidgetContractEventRow[];
} {
  const { fields, events: widgetEvents, ids: declaredIds } = parseInterface(spec);
  const usedBlockIds = collectUsedBlockIds(spec);
  const ids: WidgetContractIdRow[] = declaredIds.map((row) => ({
    ...row,
    used: usedBlockIds.has(row.id),
  }));
  const internalNames = new Set(widgetEvents.map((row) => row.name));
  const onTree = collectGlobalEventNames(spec);
  const catalog = appEvents();
  const globalEvents: WidgetContractEventRow[] = [];
  const otherGlobalEvents: WidgetContractEventRow[] = [];
  for (const event of catalog) {
    if (internalNames.has(event.name)) continue;
    const row = toGlobalRow(event);
    if (onTree.has(event.name)) globalEvents.push(row);
    else otherGlobalEvents.push(row);
  }
  globalEvents.sort((a, b) => a.name.localeCompare(b.name));
  otherGlobalEvents.sort((a, b) => a.name.localeCompare(b.name));
  return { fields, ids, widgetEvents, globalEvents, otherGlobalEvents };
}

/** Visual tree key for a contract row, or null when nothing maps. */
export function resolveWidgetContractTargetKey(
  spec: Record<string, unknown> | null | undefined,
  widgetName: string,
  phaseId: string | null | undefined,
  row: WidgetContractRow,
): string | null {
  if (!spec || !widgetName) return null;

  if (row.kind === "id") {
    return findWidgetVisualKey(spec, widgetName, phaseId, (node) => {
      return node.type === "container" && node.id === row.id;
    });
  }

  if (row.kind === "field") {
    const needle = `#{${row.name}}`;
    return findWidgetVisualKey(spec, widgetName, phaseId, (node) => {
      if (typeof node.text === "string" && node.text.includes(needle)) return true;
      const appearance = node.appearance;
      if (isRecord(appearance) && typeof appearance.text === "string" && appearance.text.includes(needle)) {
        return true;
      }
      const source = isRecord(appearance) ? appearance.source : node.source;
      if (isRecord(source) && typeof source.url === "string" && source.url.includes(needle)) return true;
      return false;
    });
  }

  return findWidgetVisualKey(spec, widgetName, phaseId, (node) => {
    return resolveInteractionEventName(node.interaction) === row.name;
  });
}

// Legacy exports used elsewhere until fully migrated.
export type WidgetEventKind = "action" | "log_id" | "show";
export interface WidgetEventRow {
  kind: WidgetEventKind;
  name: string;
}

export function resolveWidgetEventTargetKey(
  spec: Record<string, unknown> | null | undefined,
  widgetName: string,
  phaseId: string | null | undefined,
  event: WidgetEventRow,
): string | null {
  if (event.kind === "action") {
    return resolveWidgetContractTargetKey(spec, widgetName, phaseId, {
      kind: "widget_event",
      name: event.name,
      title: event.name,
    });
  }
  if (event.kind === "log_id") {
    return findWidgetVisualKey(spec, widgetName, phaseId, (node) => node.id === event.name);
  }
  if (event.kind === "show") {
    const phases = spec?.phases;
    if (!Array.isArray(phases)) return null;
    const phase = phases.find((entry) => isRecord(entry) && entry.id === event.name);
    const containerId = isRecord(phase) ? phase.container : null;
    if (typeof containerId !== "string") return null;
    return findWidgetVisualKey(spec, widgetName, phaseId, (node) => node.id === containerId);
  }
  return null;
}
